<template>
    <section class="pt-12 pb-16 font-sans text-gray-800 bg-white" :class="styleNew ? 'md-max:!pb-8' : ''">
        <div class="px-4 mx-auto max-w-5xl xl:px-0" :class="styleNew ? 'md-max:!px-8' : ''">
            <div class="flex flex-col gap-2 items-start md:gap-10 md:flex-row xl:pl-32"
                :class="styleNew ? 'md-max:gap-y-10' : ''">

                <!-- Left Content -->
                <div class="flex-1 order-2 w-full text-base leading-relaxed text-gray-900 md:text-lg md:order-1">
                    <div class="mb-4 text-lg font-bold md:mb-6 md:text-xl font-ww-medium"
                        :class="styleNew ? 'md:block hidden' : ''" v-html="headerText"></div>

                    <div class=" font-ww-regular" :class="styleNew ? 'text-xl' : 'md:text-xl text-lg'">
                        <div v-html="topContent"></div>
                    </div>
                </div>

                <!-- Right Image -->
                <div class="w-full md:max-w-[300px] flex flex-col items-center md:mt-2 md:order-2 order-1"
                    :class="styleNew ? 'md:w-[200px] ' : 'md:w-[176px] '">

                    <div class="mb-4 text-xl font-bold md:mb-6 md:text-xl font-ww-medium text-left md:hidden"
                        v-if="styleNew" v-html="headerText"></div>

                    <div class="">
                        <nuxt-img :src="imageSrc" :alt="imageAlt" width="185" height="225" class="" loading="lazy"
                            :class="styleNew ? 'md:w-[200px] ' : ''" format=" webp"
                            sizes="xs:185px sm:185px md:185px lg:185px xl:185px 2xl:185px" />
                    </div>
                    <div class="mt-2 text-center">
                        <h3 class="text-base font-bold leading-tight text-black font-ww-bold" v-html="imageCaptionName">
                        </h3>
                        <div class="text-base font-ww-regular" v-html="imageCaptionTitle"></div>
                    </div>
                </div>

            </div>

            <div class="max-w-4xl font-ww-regular xl:pl-32"
                :class="styleNew ? 'md:text-xl text-xl' : 'md:text-xl text-lg'">
                <div v-html="bottomContent"></div>
            </div>

        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    imageSrc: {
        type: String,
        default: '/assets/images/dr-right-img.png'
    },
    imageAlt: {
        type: String,
        default: 'Dr. Charles Livingston'
    },
    headerText: {
        type: String,
        default: 'From the desk of Dr. Charles Livingston:'
    },
    imageCaptionName: {
        type: String,
        default: 'Dr. Charles Livingston'
    },
    imageCaptionTitle: {
        type: String,
        default: 'Natural Health Expert'
    },
    topContent: {
        type: String,
        default: `Johns Hopkins Medicine recently reported that, according to the Dietary Guidelines for Americans, “it’s safe for most people to drink three to five cups of coffee a day with a maximum intake of 400 milligrams of caffeine.” <a href="https://www.hopkinsmedicine.org/health/wellness-and-prevention/9-reasons-why-the-right-amount-of-coffee-is-good-for-you" target="_blank" class="underline text-lifeboost-red">(source)</a>
                        <br><br>
                        They reported multiple benefits of drinking coffee, including the following:
                        <br><br>
                        <ul class="pl-5 ml-4 space-y-2 list-disc marker:text-black font-ww-regular">
                            <li>Coffee drinkers are less likely to die from some of the leading causes of death.</li>
                            <li>Coffee...contains antioxidants and other active substances that may reduce internal inflammation and protect against disease.</li>
                            <li>Dark roast coffee decreases breakage in DNA strands, which occur naturally but can lead to cancer or tumors if not repaired by your cells.</li>
                            <li>...coffee drinkers - decaf or regular - were 26 percent less likely to develop colorectal cancer.</li>
                            <li>Coffee is chock-full of substances that may help guard against...Alzheimer's disease and heart disease.</li>
                        </ul>
                        <br>`
    },
    bottomContent: {
        type: String,
        default: `With all these benefits, however, there’s a big risk in drinking coffee: pesticides, mycotoxins, and molds.
                        <br><br>
                        One research study found that 91.7% of the coffee samples they tested contained molds. <a href="https://pubmed.ncbi.nlm.nih.gov/14726276/" target="_blank" class="underline text-lifeboost-red">(source)</a>
                        <br><br>
                        So what do you do?
                        <br>
                        It was to that coffee that I’ve compared everything else.
                        <br><br>
                        This coffee is clean, healthy, non-GMO, and as pure as you can get with just one simple ingredient:`
    },
    styleNew: {
        type: Boolean,
        default: false
    }
});

defineBuilderComponent({
    friendlyName: 'Left Info With Image',
    inputs: [
        {
            name: 'styleNew',
            type: 'boolean',
            friendlyName: 'Style New',
            defaultValue: false
        },
        {
            name: 'headerText',
            type: 'richText',
            friendlyName: 'Header Text',
            defaultValue: 'From the desk of Dr. Charles Livingston:'
        },
        {
            name: 'imageSrc',
            type: 'file',
            friendlyName: 'Image',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/dr-right-img.png'
        },
        {
            name: 'imageAlt',
            type: 'text',
            friendlyName: 'Image Alt Text',
            defaultValue: 'Dr. Charles Livingston'
        },
        {
            name: 'imageCaptionName',
            type: 'text',
            friendlyName: 'Caption Name',
            defaultValue: 'Dr. Charles Livingston'
        },
        {
            name: 'imageCaptionTitle',
            type: 'text',
            friendlyName: 'Caption Title',
            defaultValue: 'Natural Health Expert'
        },
        {
            name: 'topContent',
            type: 'richText',
            friendlyName: 'Top Content',
            defaultValue: `Johns Hopkins Medicine recently reported that, according to the Dietary Guidelines for Americans, “it’s safe for most people to drink three to five cups of coffee a day with a maximum intake of 400 milligrams of caffeine.” <a href="https://www.hopkinsmedicine.org/health/wellness-and-prevention/9-reasons-why-the-right-amount-of-coffee-is-good-for-you" target="_blank" class="underline text-lifeboost-red">(source)</a>
                        <br><br>
                        They reported multiple benefits of drinking coffee, including the following:
                        <br><br>
                        <ul class="pl-5 ml-4 space-y-2 list-disc marker:text-black font-ww-regular">
                            <li>Coffee drinkers are less likely to die from some of the leading causes of death.</li>
                            <li>Coffee...contains antioxidants and other active substances that may reduce internal inflammation and protect against disease.</li>
                            <li>Dark roast coffee decreases breakage in DNA strands, which occur naturally but can lead to cancer or tumors if not repaired by your cells.</li>
                            <li>...coffee drinkers - decaf or regular - were 26 percent less likely to develop colorectal cancer.</li>
                            <li>Coffee is chock-full of substances that may help guard against...Alzheimer's disease and heart disease.</li>
                        </ul>
                        <br>`
        },
        {
            name: 'bottomContent',
            type: 'richText',
            friendlyName: 'Bottom Content',
            defaultValue: `With all these benefits, however, there’s a big risk in drinking coffee: pesticides, mycotoxins, and molds.
                        <br><br>
                        One research study found that 91.7% of the coffee samples they tested contained molds. <a href="https://pubmed.ncbi.nlm.nih.gov/14726276/" target="_blank" class="underline text-lifeboost-red">(source)</a>
                        <br><br>
                        So what do you do?
                        <br>
                        It was to that coffee that I’ve compared everything else.
                        <br><br>
                        This coffee is clean, healthy, non-GMO, and as pure as you can get with just one simple ingredient:`
        }
    ]
});
</script>